import { z } from "zod";
import { uuidSchema } from "./common";

// Tip types are an enum so FE can pick icon + i18n message keys without
// trusting the BE-rendered string. BE returns a fallback `message` for safety.
export const insightTipTypeSchema = z.enum([
  "STALLED_NO_APPLICANTS", // posting with 0 applicants and >N days since posting
  "LOW_SALARY_VS_PEERS", // posting salary below avg in same position
  "MISSING_ORG_BANNER", // org has no banner image
  "MISSING_POSTING_BENEFITS", // posting has no benefits listed
  "LOW_CREDITS", // org's interview credits running low
]);
export type InsightTipType = z.infer<typeof insightTipTypeSchema>;

export const insightTipSchema = z.object({
  id: z.string(), // stable id for FE dismissal/localStorage
  type: insightTipTypeSchema,
  // Human-readable Vietnamese fallback. FE may render its own copy keyed by type.
  message: z.string(),
  // When the tip targets a specific posting, FE can render it inline below that card.
  postingId: uuidSchema.nullable().optional(),
  // Suggested action — FE wires this to the appropriate route/modal.
  cta: z
    .enum(["EDIT_POSTING", "EDIT_ORG", "BUY_CREDITS"])
    .nullable()
    .optional(),
  // Optional structured payload (e.g. salary benchmark numbers) so FE can
  // render a richer copy without parsing `message`.
  data: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type InsightTip = z.infer<typeof insightTipSchema>;

export const orgInsightsResponseSchema = z.object({
  week: z.object({
    // Application.appliedAt within last 7 days, across all postings of the org,
    // excluding WITHDRAWN.
    applicants: z.number().int().nonnegative(),
    // Invite.createdAt within last 7 days (interview slots offered).
    interviews: z.number().int().nonnegative(),
  }),
  credits: z.object({
    available: z.number().int().nonnegative(),
    locked: z.number().int().nonnegative(),
  }),
  // Anti-spam quota: how many OPEN postings the org currently has vs the max
  // allowed for its verification tier. `verified` lets the UI nudge unverified
  // orgs to KYC for a higher cap.
  postingQuota: z.object({
    open: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    verified: z.boolean(),
  }),
  tips: z.array(insightTipSchema),
});
export type OrgInsightsResponse = z.infer<typeof orgInsightsResponseSchema>;
